import { Shield, AlertTriangle, BarChart3, Brain, Zap, FileText, CheckCircle2, BookOpen, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';

export default function UserGuide() {
  return (
    <div className="min-h-screen bg-zinc-50 py-6 px-4" data-testid="user-guide-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="text-blue-600" size={24} />
            <h1 className="text-2xl font-bold text-zinc-900">User Guide</h1>
          </div>
          <p className="text-sm text-zinc-600">
            Complete guide to using TRACK CC fraud detection system
          </p>
        </div>

        {/* Disclaimer */}
        <Card className="p-4 bg-amber-50 border-amber-200 mb-6" data-testid="disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Simulation Only</p>
              <p className="text-xs text-amber-700 mt-1">
                This is a working demonstration. Do not upload actual production data. 
                This application does not address all applicable financial regulations 
                (PCI-DSS, GDPR, SOX, etc.). For production use, additional compliance 
                measures are required.
              </p>
            </div>
          </div>
        </Card>

        {/* 3 Detection Approaches */}
        <Card className="p-5 bg-white mb-6" data-testid="detection-approaches">
          <h2 className="text-base font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <Shield size={18} className="text-purple-600" />
            3 Detection Approaches
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Rule-Based */}
            <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-zinc-300 flex items-center justify-center text-sm font-bold text-zinc-700">1</div>
                <span className="text-sm font-bold text-zinc-800">Rule-Based</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                Default detection mode using predefined rules. Scores transactions based on:
              </p>
              <ul className="text-xs text-zinc-600 space-y-1 mb-3">
                <li>• Transaction amount thresholds</li>
                <li>• Time of day patterns</li>
                <li>• Transaction frequency</li>
                <li>• Merchant risk categories</li>
              </ul>
              <div className="px-3 py-1.5 bg-zinc-200 rounded-lg text-xs text-zinc-600 inline-block">
                Always Available
              </div>
            </div>

            {/* Emergent ML */}
            <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-300 ring-2 ring-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white">2</div>
                <span className="text-sm font-bold text-zinc-800">Emergent ML</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                <strong>Pre-trained Gradient Boosting</strong> model ready to use. Uses Mini Gradient Boosting 
                (XGBoost-style algorithm) for intelligent fraud detection.
              </p>
              <ul className="text-xs text-zinc-600 space-y-1 mb-3">
                <li>• ~83% accuracy on test data</li>
                <li>• 50 decision trees ensemble</li>
                <li>• <strong>Ready out of the box</strong></li>
                <li>• Includes ML explainability</li>
              </ul>
              <div className="px-3 py-1.5 bg-blue-500 rounded-lg text-xs text-white font-semibold inline-block">
                Pre-trained & Ready
              </div>
            </div>

            {/* Custom API */}
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-sm font-bold text-white">3</div>
                <span className="text-sm font-bold text-zinc-800">Custom API</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                Connect your own ML service via REST API. Works with any backend: 
                AWS SageMaker, Google Vertex AI, Azure ML, or self-hosted.
              </p>
              <ul className="text-xs text-zinc-600 space-y-1 mb-3">
                <li>• Any ML framework (XGBoost, TensorFlow, PyTorch)</li>
                <li>• Your own training data</li>
                <li>• Full model control</li>
                <li>• Enterprise-grade flexibility</li>
              </ul>
              <div className="px-3 py-1.5 bg-emerald-500 rounded-lg text-xs text-white font-semibold inline-block">
                Enterprise
              </div>
            </div>
          </div>
        </Card>

        {/* How to Use */}
        <Card className="p-5 bg-white mb-6" data-testid="how-to-use">
          <h2 className="text-base font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <Zap size={18} className="text-amber-600" />
            How to Use
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">Authenticate</p>
                <p className="text-xs text-zinc-600 mt-1">
                  Use face recognition to log in, or click "Demo Login" if camera access is denied. 
                  Sessions are required for each visit.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">View Dashboard</p>
                <p className="text-xs text-zinc-600 mt-1">
                  See real-time transaction monitoring with live stats, charts, and flagged transactions. 
                  Data updates via WebSocket for instant visibility.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">ML Model Pre-Trained</p>
                <p className="text-xs text-zinc-600 mt-1">
                  The Gradient Boosting model comes <strong>pre-trained and ready to use</strong>. 
                  Visit <strong>Training</strong> page to see metrics or retrain if needed.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">Or Connect Custom API</p>
                <p className="text-xs text-zinc-600 mt-1">
                  On the Training page, enter your REST API endpoint URL to use your 
                  own ML service (SageMaker, Vertex AI, or self-hosted).
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">5</div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">Integrate via API</p>
                <p className="text-xs text-zinc-600 mt-1">
                  Visit the <strong>API</strong> page for documentation on integrating fraud detection 
                  into your payment systems using REST endpoints.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Dashboard Features */}
          <Card className="p-5 bg-white" data-testid="dashboard-features">
            <h3 className="text-sm font-bold text-zinc-900 mb-3 flex items-center gap-2">
              <BarChart3 size={16} className="text-blue-600" />
              Dashboard Features
            </h3>
            <ul className="text-xs text-zinc-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Live Transaction Stream</strong> - Real-time updates via WebSocket</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Stats Cards</strong> - Total, Fraud Count, Fraud Rate, Blocked</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Bar Chart</strong> - Transactions by merchant with fraud overlay</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Pie Chart</strong> - Blocked transactions by reason</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Flagged List</strong> - Recent alerts with ML explanations</span>
              </li>
            </ul>
          </Card>

          {/* Fraud Scoring */}
          <Card className="p-5 bg-white" data-testid="fraud-scoring">
            <h3 className="text-sm font-bold text-zinc-900 mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              Fraud Score Thresholds
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div>
                  <p className="text-xs font-bold text-red-700">BLOCKED (Score &gt; 0.70)</p>
                  <p className="text-[10px] text-red-600">High-risk transaction automatically blocked</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <div>
                  <p className="text-xs font-bold text-amber-700">FLAGGED (Score 0.50 - 0.70)</p>
                  <p className="text-[10px] text-amber-600">Suspicious transaction requires review</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <div>
                  <p className="text-xs font-bold text-emerald-700">APPROVED (Score &lt; 0.50)</p>
                  <p className="text-[10px] text-emerald-600">Safe transaction allowed to proceed</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Risk Factors */}
          <Card className="p-5 bg-white" data-testid="risk-factors">
            <h3 className="text-sm font-bold text-zinc-900 mb-3 flex items-center gap-2">
              <Brain size={16} className="text-purple-600" />
              Risk Factors Analyzed
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
                <span className="text-xs font-medium text-zinc-700">Transaction Amount</span>
                <span className="text-[10px] text-zinc-500">Higher = More Risk</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
                <span className="text-xs font-medium text-zinc-700">Time of Day</span>
                <span className="text-[10px] text-zinc-500">12am-5am = Higher Risk</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
                <span className="text-xs font-medium text-zinc-700">Transaction Frequency</span>
                <span className="text-[10px] text-zinc-500">Many/day = Suspicious</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
                <span className="text-xs font-medium text-zinc-700">Merchant Category</span>
                <span className="text-[10px] text-zinc-500">Casino, Crypto = High Risk</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-zinc-50 rounded">
                <span className="text-xs font-medium text-zinc-700">Location</span>
                <span className="text-[10px] text-zinc-500">Certain regions flagged</span>
              </div>
            </div>
          </Card>

          {/* ML Explainability */}
          <Card className="p-5 bg-white" data-testid="explainability">
            <h3 className="text-sm font-bold text-zinc-900 mb-3 flex items-center gap-2">
              <FileText size={16} className="text-emerald-600" />
              ML Explainability
            </h3>
            <p className="text-xs text-zinc-600 leading-relaxed mb-3">
              Every prediction includes an explanation showing which factors contributed 
              most to the fraud score. This helps analysts understand and trust the model's decisions.
            </p>
            <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
              <p className="text-[10px] font-semibold text-zinc-700 mb-2">Example Explanation:</p>
              <div className="flex flex-wrap gap-2">
                <span className="text-[10px] px-2 py-1 bg-red-100 text-red-700 rounded-full">
                  Amount: $5000 ↑32%
                </span>
                <span className="text-[10px] px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                  Merchant Risk ↑18%
                </span>
                <span className="text-[10px] px-2 py-1 bg-green-100 text-green-700 rounded-full">
                  Time: 2pm ↓5%
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Links */}
        <Card className="p-5 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200" data-testid="quick-links">
          <h3 className="text-sm font-bold text-zinc-900 mb-3">Quick Links</h3>
          <div className="flex flex-wrap gap-3">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              <BarChart3 size={14} />
              Dashboard
              <ArrowRight size={12} />
            </Link>
            <Link 
              to="/training" 
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              <Brain size={14} />
              Training
              <ArrowRight size={12} />
            </Link>
            <Link 
              to="/integration" 
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              <FileText size={14} />
              API Docs
              <ArrowRight size={12} />
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
